import { z } from "zod";

/**
 * Domain vocabulary for the outbound pipeline.
 *
 * These enums mirror the Postgres enums in @pinpoint/db one-for-one. They are
 * duplicated deliberately: the database owns storage, this file owns the wire
 * format, and the compiler catches drift the moment one side changes because
 * the Worker assigns values from here into rows typed by there.
 */

// --- Strategies -------------------------------------------------------------

export const strategyTypeSchema = z.enum([
  "sourcing",
  "contact_finding",
  "enrichment",
  "outreach",
  "sending",
  "meta",
]);
export type StrategyType = z.infer<typeof strategyTypeSchema>;

export const strategyStatusSchema = z.enum([
  "draft",
  "active",
  "paused",
  "archived",
]);
export type StrategyStatus = z.infer<typeof strategyStatusSchema>;

/** `sourcing/competitor-mining` — a type segment, then a slug. */
export const strategyKeySchema = z
  .string()
  .min(3)
  .max(120)
  .regex(
    /^[a-z_]+\/[a-z0-9-]+$/,
    "Expected a key like `sourcing/competitor-mining`.",
  );

// --- Runs -------------------------------------------------------------------

export const runStatusSchema = z.enum([
  "running",
  "succeeded",
  "failed",
  "abandoned",
]);
export type RunStatus = z.infer<typeof runStatusSchema>;

/** Terminal statuses a run can be moved into by `finish_run`. */
export const runFinishStatusSchema = z.enum([
  "succeeded",
  "failed",
  "abandoned",
]);

// --- Companies --------------------------------------------------------------

export const sourceTypeSchema = z.enum(["cold", "warm"]);
export type SourceType = z.infer<typeof sourceTypeSchema>;

/**
 * Pipeline stages in funnel order.
 *
 * The order is the report: docs/02 §3.6 measures conversion between adjacent
 * entries, so anything appended here shows up as a new funnel step. The three
 * exits (`parked`, `killed`, and `converted` as the win) sit outside the
 * progression and are excluded from drop-off maths by `FUNNEL_STAGES`.
 */
export const COMPANY_STAGE_ORDER = [
  "sourced",
  "qualified",
  "enriching",
  "enriched",
  "drafting",
  "in_review",
  "approved",
  "sending",
  "active",
  "replied",
  "converted",
] as const;

export const companyStageSchema = z.enum([
  ...COMPANY_STAGE_ORDER,
  "parked",
  "killed",
]);
export type CompanyStage = z.infer<typeof companyStageSchema>;

/** Stages that count as steps in the funnel report, in order. */
export const FUNNEL_STAGES = COMPANY_STAGE_ORDER;

/** Exits that stop a company progressing; both demand a reason. */
export const TERMINAL_STAGES = ["parked", "killed"] as const;

/**
 * Legal stage transitions.
 *
 * Written out rather than derived from the order because real pipelines move
 * backwards: a reviewer rejecting every draft should send a company back to
 * `enriched` for a fresh angle, not strand it in `in_review`. What is *not*
 * allowed is skipping the review gate — nothing reaches `sending` except from
 * `approved`, which is the stage machine's half of README constraint 2.
 */
export const COMPANY_STAGE_TRANSITIONS: Record<
  CompanyStage,
  readonly CompanyStage[]
> = {
  sourced: ["qualified", "parked", "killed"],
  qualified: ["enriching", "enriched", "sourced", "parked", "killed"],
  enriching: ["enriched", "qualified", "parked", "killed"],
  // `in_review` is reachable directly from `enriched` because `create_sequence`
  // produces a draft that is *already* awaiting a human — an agent that drafts
  // in one call never passes through a `drafting` state anyone could observe.
  enriched: ["drafting", "in_review", "qualified", "parked", "killed"],
  drafting: ["in_review", "enriched", "parked", "killed"],
  in_review: ["approved", "drafting", "enriched", "parked", "killed"],
  approved: ["sending", "in_review", "parked", "killed"],
  sending: ["active", "approved", "parked", "killed"],
  active: ["replied", "converted", "parked", "killed"],
  replied: ["converted", "active", "parked", "killed"],
  converted: ["active", "parked"],
  // Revival returns a company to the top of the funnel rather than to wherever
  // it left off — its research is stale by definition and should be re-judged.
  parked: ["sourced", "qualified", "killed"],
  killed: ["sourced", "qualified", "parked"],
};

export function canTransitionCompany(
  from: CompanyStage,
  to: CompanyStage,
): boolean {
  if (from === to) return true;
  return COMPANY_STAGE_TRANSITIONS[from].includes(to);
}

/** Parking or killing a company without saying why teaches the strategy nothing. */
export function stageRequiresReason(stage: CompanyStage): boolean {
  return (TERMINAL_STAGES as readonly CompanyStage[]).includes(stage);
}

// --- Contacts ---------------------------------------------------------------

export const contactStatusSchema = z.enum([
  "identified",
  "enriched",
  "sequenced",
  "replied",
  "unsubscribed",
  "suppressed",
  "dead",
]);
export type ContactStatus = z.infer<typeof contactStatusSchema>;

export const emailStatusSchema = z.enum([
  "found",
  "guessed",
  "verified",
  "bounced",
]);
export type EmailStatus = z.infer<typeof emailStatusSchema>;

// --- Sequences --------------------------------------------------------------

export const sequenceStatusSchema = z.enum([
  "draft",
  "in_review",
  "approved",
  "rejected",
  "superseded",
  "queued",
  "sending",
  "sent",
  "stopped",
  "completed",
]);
export type SequenceStatus = z.infer<typeof sequenceStatusSchema>;

export const sequenceEmailStatusSchema = z.enum(["pending", "sent", "skipped"]);

export const reviewActionSchema = z.enum([
  "approved",
  "approved_with_edits",
  "rejected",
  "variant_selected",
]);
export type ReviewAction = z.infer<typeof reviewActionSchema>;

/**
 * The rejection taxonomy from docs/04 O1's quality bar.
 *
 * Fixed options rather than free text because these are the numbers a
 * strategist acts on — "38% rejected for `too_long`" is a playbook edit,
 * while a pile of prose is a reading exercise. Free text rides alongside.
 */
export const rejectReasonSchema = z.enum([
  "wrong_facts",
  "weak_personalization",
  "too_long",
  "wrong_tone",
  "wrong_social_proof",
  "bad_targeting",
  "generic",
  "other",
]);
export type RejectReason = z.infer<typeof rejectReasonSchema>;

export const outcomeKindSchema = z.enum([
  "open",
  "reply",
  "positive_reply",
  "meeting",
  "unsubscribe",
  "bounce",
]);
export type OutcomeKind = z.infer<typeof outcomeKindSchema>;

// --- Suppression ------------------------------------------------------------

export const suppressionKindSchema = z.enum(["email", "domain"]);
export const suppressionReasonSchema = z.enum([
  "hubspot",
  "unsubscribed",
  "bounced",
  "manual",
]);

// --- Research payloads ------------------------------------------------------

/**
 * One researched fact. `sourceUrl` is what makes it checkable and `assumption`
 * is what keeps an agent honest when it isn't — docs/04 E1 requires unsourced
 * claims to be labelled rather than asserted.
 */
export const researchFactSchema = z.object({
  value: z.unknown(),
  sourceUrl: z.string().url().nullish(),
  collectedAt: z.string().datetime().optional(),
  assumption: z.boolean().optional(),
  note: z.string().max(500).nullish(),
});
export type ResearchFactInput = z.infer<typeof researchFactSchema>;

export const enrichmentFactsSchema = z.record(researchFactSchema);

export const warmTriggerSchema = z.object({
  kind: z.string().min(1).max(80),
  detail: z.string().max(2000).nullish(),
  occurredAt: z.string().datetime().nullish(),
  hubspotRef: z.string().max(120).nullish(),
});

/** Enrichment older than this is flagged "may be stale" (docs/02 §5). */
export const ENRICHMENT_STALE_DAYS = 30;

// --- Shared field schemas ---------------------------------------------------

/**
 * A bare registrable domain. Agents habitually pass a full URL or a `www.`
 * host, and since the domain is the dedupe key, three spellings of one company
 * would become three companies. Normalising in the parser rather than in each
 * caller means every entry point dedupes identically.
 */
export const domainSchema = z
  .string()
  .trim()
  .min(3)
  .max(255)
  .transform((value) =>
    value
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .split("/")[0]!
      .split("?")[0]!
      .replace(/\.$/, ""),
  )
  .pipe(
    z
      .string()
      .regex(/^[a-z0-9-]+(\.[a-z0-9-]+)+$/, "Expected a domain like acme.co.uk"),
  );

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(320);

/** 0–100. Scores exist to rank a work queue, so the range must be comparable. */
export const scoreSchema = z.number().int().min(0).max(100);

export const markdownSchema = z.string().max(20000);

// --- Staged imports ---------------------------------------------------------

export const importKindSchema = z.enum(["companies", "reference_clients"]);
export type ImportKind = z.infer<typeof importKindSchema>;

export const importStatusSchema = z.enum([
  "pending",
  "processing",
  "processed",
  "failed",
]);
export type ImportStatus = z.infer<typeof importStatusSchema>;

/**
 * A researched reference client.
 *
 * Only `name` is required, because the human pasting a list has only names —
 * everything else is what an agent is expected to find. A client with no
 * `researchedAt` has not been looked at yet, and the UI says so rather than
 * rendering blanks as though they were answers.
 */
export const referenceClientSchema = z.object({
  name: z.string().min(1).max(200),
  domain: z.string().max(255).nullish(),
  industry: z.string().max(120).nullish(),
  sizeBand: z.string().max(40).nullish(),
  employeeCount: z.number().int().min(0).max(10_000_000).nullish(),
  region: z.string().max(120).nullish(),
  /** Why they are a useful reference — the angle outreach can lean on. */
  note: z.string().max(2000).nullish(),
  sourceUrl: z.string().url().max(500).nullish(),
  researchedAt: z.string().datetime().nullish(),
  runId: z.string().uuid().nullish(),
});
export type ReferenceClient = z.infer<typeof referenceClientSchema>;

/** Stored under the `reference_clients` app setting. */
export interface ReferenceClientsSetting {
  /** Exactly what the human pasted, kept for audit. */
  raw?: string;
  clients: ReferenceClient[];
}

/** Read the setting tolerantly — early rows were a bare array of clients. */
export function readReferenceClients(value: unknown): ReferenceClientsSetting {
  if (Array.isArray(value)) return { clients: value as ReferenceClient[] };
  if (value && typeof value === "object") {
    const obj = value as Partial<ReferenceClientsSetting>;
    return { raw: obj.raw, clients: obj.clients ?? [] };
  }
  return { clients: [] };
}
